import React, { useState, useEffect } from 'react';
import TyreService from '../services/TyreService';
import '../scss/TyreFilters.scss';
import { SEASON } from './TyreLabel';

const TyreFilters = ({ onFilterChange, onSearch, isWidget = false, initialFilters = {} }) => {
  const [filters, setFilters] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    brand: '',
    width: '',
    height: '',
    radius: '',
    season: [], // ← Changé de '' vers []
    category: '',
    runflat: '',
    reinforced: '',
    speedIndex: '',
    loadIndex: '',
    ...initialFilters
  });
  const [loading, setLoading] = useState(true);
  const [isUpdatingFilters, setIsUpdatingFilters] = useState(false);
  const [error, setError] = useState(null);
  const [isFallbackMode, setIsFallbackMode] = useState(false);

  useEffect(() => {
    loadFilters();
  }, []);

  useEffect(() => {
    if (Object.keys(initialFilters).length > 0) {
      setSelectedFilters(prev => ({
        ...prev,
        ...initialFilters
      }));
    }
  }, [initialFilters]);

  // Écouter l'événement de sélection de centre pour rouvrir la modal
  useEffect(() => {
    const handleStoreSelected = (event) => {
      // Rouvrir la modal de filtre si c'est le widget
      if (isWidget) {
        const homeDevis = document.getElementById('home-devis');
        if (homeDevis) {
          homeDevis.classList.add('active');
        }
      }
      // Effacer le message d'erreur
      setError(null);
    };

    document.addEventListener('ax:storeSelected', handleStoreSelected);

    return () => {
      document.removeEventListener('ax:storeSelected', handleStoreSelected);
    };
  }, [isWidget]);

  const loadFilters = async (params = {}) => {
    try {
      setIsUpdatingFilters(true);
      const data = await TyreService.getFilters(params);
      setFilters(data);
      // Détecter si on est en mode fallback (l'adhérent n'a pas accès aux devis)
      setIsFallbackMode(data.isFallbackMode || false);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des filtres');
      setLoading(false);
    } finally {
      setIsUpdatingFilters(false);
    }
  };

  const handleFilterChange = async (filterName, value) => {
    const newFilters = {
      ...selectedFilters,
      [filterName]: value
    };
    setSelectedFilters(newFilters);
    
    // Rechargement des filtres disponibles en fonction des sélections
    const filterParams = Object.entries(newFilters)
      .filter(([_, val]) => val !== '')
      .reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {});
      
    await loadFilters(filterParams);
  };

  // Fonction helper pour rendre un select
  const renderSelect = (name, label, options, optionGroups = null) => (
    <div className="filter-field">
      <label>{label}</label>
      <div className={`select-wrapper ${isUpdatingFilters ? 'loading' : ''}`}>
        <select 
          value={selectedFilters[name]}
          onChange={(e) => handleFilterChange(name, e.target.value)}
          disabled={isUpdatingFilters}
        >
          <option value="">Tout</option>
          {optionGroups ? (
            Object.entries(optionGroups).map(([category, items]) => (
              <optgroup key={category} label={category}>
                {items.map(item => (
                  <option key={item} value={item}>{item}</option>
                ))}
              </optgroup>
            ))
          ) : (
            options?.map(item => (
              <option key={item} value={item}>{item}</option>
            ))
          )}
        </select>
        {isUpdatingFilters && <div className="select-loader"></div>}
      </div>
    </div>
  );

  // Fonction helper pour rendre des checkboxes
  const renderCheckboxes = (name, label, options) => {
	const getSeasonClass = (index) => {
        switch(index) {
            case 0: return 'summer';
            case 1: return 'winter';  
            case 2: return 'all';  // ← Je suppose que c'est 2 et non 3 ?
            default: return '';
        }
    };

	return (
		<div className="filter-field seasons">
		<label>{label}</label>
		<div className="filters-row">
			{options?.map((option, index) => {
				const seasonClass = getSeasonClass(index);
				return (
					<label key={option} className={`checkbox-field ${seasonClass}`}>
						<input
						type="checkbox"
						checked={selectedFilters[name].includes(option)}
						onChange={(e) => handleCheckboxChange(name, option, e.target.checked)}
						disabled={isUpdatingFilters}
						/>
						{SEASON[option] || option}
					</label>
				);
  			})}
		</div>
		</div>
	);
};

  const handleCheckboxChange = async (filterName, value, isChecked) => {
	const currentValues = selectedFilters[filterName] || [];
	// S'assurer que currentValues est toujours un tableau
	const currentValuesArray = Array.isArray(currentValues) ? currentValues : [];
	let newValues;
	
	if (isChecked) {
		// Ajouter la valeur si elle n'existe pas déjà
		newValues = currentValuesArray.includes(value) 
			? currentValuesArray 
			: [...currentValuesArray, value];
	} else {
		// Retirer la valeur
		newValues = currentValuesArray.filter(v => v !== value);
	}
    
    const newFilters = {
      ...selectedFilters,
      [filterName]: newValues
    };
    setSelectedFilters(newFilters);
    
    // Rechargement des filtres disponibles en fonction des sélections
    const filterParams = Object.entries(newFilters)
      .filter(([_, val]) => {
        // Pour les arrays, vérifier s'ils ne sont pas vides
        return Array.isArray(val) ? val.length > 0 : val !== '';
      })
      .reduce((acc, [key, val]) => ({ 
        ...acc, 
        [key]: Array.isArray(val) ? val.join(',') : val 
      }), {});
      
    await loadFilters(filterParams);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Vérification du code adhérent et du cookie favorite_store
    const adherentCode = window.axTyresCatalog?.adherentCode || '';
    const favoriteStoreCookie = document.cookie.split('; ').find(row => row.startsWith('favorite_store='));
    const hasFavoriteStore = favoriteStoreCookie && favoriteStoreCookie.split('=')[1];
    
    if (!adherentCode && !hasFavoriteStore) {
      // Ouvrir la modal de sélection de centre
      const modal = document.getElementById('ax-storelocator-modal');
      if (modal && window.jQuery) {
        window.jQuery(modal).modal('show');
      }
      setError('Veuillez sélectionner un centre Eurotyre pour continuer votre recherche.');
      return;
    }

    const activeFilters = Object.entries(selectedFilters)
      .filter(([_, value]) => {
        // Pour les arrays, vérifier s'ils ne sont pas vides
        return Array.isArray(value) ? value.length > 0 : value !== '';
      })
      .reduce((acc, [key, value]) => ({ 
        ...acc, 
        [key]: Array.isArray(value) ? value.join(',') : value 
      }), {});

    // Si on est en mode fallback, rediriger vers le formulaire de contact
    if (isFallbackMode) {
      // Construire le message avec les critères de recherche
      let message = 'Bonjour,\n\nJe cherche un pneu avec les critères suivants :\n\n';
      
      // Dimensions
      const dimensions = `${selectedFilters.width || '-'}/${selectedFilters.height || '-'} R${selectedFilters.radius || '-'}`;
      message += `- Dimensions : ${dimensions}\n`;
      
      // Marque
      if (selectedFilters.brand) {
        message += `- Marque : ${selectedFilters.brand}\n`;
      }
      
      // Catégorie
      if (selectedFilters.category) {
        message += `- Catégorie : ${selectedFilters.category}\n`;
      }
      
      // Saison
      if (selectedFilters.season && selectedFilters.season.length > 0) {
        const seasonLabels = selectedFilters.season.map(s => SEASON[s] || s).join(', ');
        message += `- Saison : ${seasonLabels}\n`;
      }
      
      // Indice de charge
      if (selectedFilters.loadIndex) {
        message += `- Indice de charge : ${selectedFilters.loadIndex}\n`;
      }
      
      // Indice de vitesse
      if (selectedFilters.speedIndex) {
        message += `- Indice de vitesse : ${selectedFilters.speedIndex}\n`;
      }
      
      // Runflat
      if (selectedFilters.runflat === 'true') {
        message += `- Runflat : Oui\n`;
      }
      
      // Renforcé
      if (selectedFilters.reinforced === 'true') {
        message += `- Renforcé : Oui\n`;
      }
      
      message += '\nMerci de me contacter pour établir un devis.';
      
      // Construire l'URL du formulaire de contact avec les paramètres
      const favoriteStoreUrl = window.axTyresCatalog?.favoriteStoreUrl || '';
      let contactUrl = favoriteStoreUrl 
        ? `${favoriteStoreUrl.replace(/\/?$/, '/')}/contact/` 
        : '/contact/';
      
      // Remove any double slashes except after http(s):
      contactUrl = contactUrl.replace(/([^:]\/)\/+/g, "$1");
      
      // Ajouter le message comme paramètre GET
      const urlParams = new URLSearchParams({
        ax_contact_message: message,
        ax_contact_motif: 'Devis pneumatique'
      });
      
      // Rediriger vers le formulaire de contact
      window.location.href = `${contactUrl}?${urlParams.toString()}`;
      return;
    }

    // Si nous sommes dans le widget, nous devons gérer la redirection nous-mêmes
    if (isWidget) {
      const searchParams = new URLSearchParams();
      Object.entries(activeFilters).forEach(([key, value]) => {
        if (value) {
          searchParams.append(key, value);
        }
      });

      // Get the favorite store URL from the global variable
      const favoriteStoreUrl = window.axTyresCatalog?.favoriteStoreUrl || '';
      
      // Build the catalog URL
      let catalogUrl = favoriteStoreUrl 
        ? `${favoriteStoreUrl.replace(/\/?$/, '/')}/catalogue-pneus/` 
        : '/catalogue-pneus/';
      
      // Remove any double slashes except after http(s):
      catalogUrl = catalogUrl.replace(/([^:]\/)\/+/g, "$1");
      
      // Redirect to the catalog page with the search parameters
      window.location.href = `${catalogUrl}?${searchParams.toString()}`;
    } else {
      // Si nous sommes dans la page catalogue, utiliser les callbacks normalement
      onFilterChange(activeFilters);
      onSearch(activeFilters);
    }
  };

  const isTyreCatalog = document.querySelector('#ax-tyres-catalog-root') instanceof HTMLElement;

  const handleReset = () => {
    // Simple reload de la page pour reset tous les filtres
	window.location.href = window.location.pathname;
  };

  const handleClose = () => {
    const homeDevis = document.getElementById('home-devis');
	if (homeDevis) {
		homeDevis.classList.remove('active');
	}
  };

  if (loading) return <div className="filters-loading">Chargement des filtres...</div>;
  if (error) return <div className="filters-error">{error}</div>;
  if (!filters) return null;
  let decalage = -63;
  if (window.innerWidth < 768) {
	decalage = -36;
  }

  return (
    <div className={`tyre-filters ${isWidget ? 'widget-mode' : ''}`}>
		<div 
			className="home-devis-content-close btn"
			onClick={handleClose}
		>
			<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path d="M18 6L6 18" stroke="#000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
				<path d="M6 6L18 18" stroke="#000" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"/>
			</svg>
		</div>
      <div className='popin-title'>{isWidget ? 'Trouver mes pneus par dimension' : 'Trouver mes pneus par dimension'}</div>
	  <div className="filter-values-wrapper">
		<img 
			src="/wp-content/plugins/ax_tyres_catalog/app/public/svg/pneu-devis.png" 
			alt="Description de l'image" 
		/>
		<svg width="400" height="100" viewBox={`0 ${decalage} 400 100`}>
			<defs>
				<path id="arc" d="M 30 150 A 170 170 0 0 1 370 150"></path>
			</defs>
			<text text-anchor="middle">
				<textPath href="#arc" startOffset="50%">
				{selectedFilters.width ? selectedFilters.width : '-'}/{selectedFilters.height ? selectedFilters.height : '-'} R{selectedFilters.radius ? selectedFilters.radius : '-'}
				</textPath>
			</text>
		</svg>
	  </div>
      <form className="filters-form" onSubmit={handleSubmit}>
        {/* Dimensions */}
        <div className="filters-section">
          <div className="filters-row">
            {renderSelect('width', 'Largeur *', filters.widths)}
            {renderSelect('height', 'Hauteur *', filters.heights)}
            {renderSelect('radius', 'Diamètre *', filters.radius)}
			{renderSelect('loadIndex', 'Charge', filters.loadIndexes)}
            {renderSelect('speedIndex', 'Vitesse', filters.speedIndexes)}
          </div>
        </div>

        {/* Caractéristiques */}
        <div className="filters-section">
          <div className="filters-row">
            {renderSelect('brand', 'Marque', null, filters.brandByCategory)}
			<div className="filter-field spec">
				<label>Spécificités</label>
				<div className="filters-row">
					<label className="checkbox-field">
						<input
							type="checkbox"
							checked={selectedFilters.runflat === 'true'}
							onChange={(e) => handleFilterChange('runflat', e.target.checked.toString())}
							disabled={isUpdatingFilters}
						/>
						Runflat
						<span 
							class="info-tooltip" 
							data-title="Permet de rouler à plat sur une distance limitée et à vitesse réduite (technologie spécifique à certains véhicules, à monter par 4)">
						</span>
					</label>
					<label className="checkbox-field">
						<input
							type="checkbox"
							checked={selectedFilters.reinforced === 'true'}
							onChange={(e) => handleFilterChange('reinforced', e.target.checked.toString())}
							disabled={isUpdatingFilters}
						/>
						Renforcé
						<span 
							class="info-tooltip" 
							data-title="Pneu renforcé ayant un indice de charge supérieur">
						</span>
					</label>
				</div>
			</div>
			{renderCheckboxes('season', 'Type de saison', filters.seasons)}
          </div>
        </div>

        {/* Options */}

        {/* Boutons d'action */}
        <div className="filters-actions">
			{isTyreCatalog && (
				<button type="button" class="quote-button quote-button-photo" data-bs-toggle="modal" data-bs-target="#tyremotionModal">
					<div>
						Par photographie
						<div>Prenez votre roue en photo</div>
					</div>
				</button>
			)}
			<button type="submit" className="btn btn-primary search" disabled={isUpdatingFilters}>
				Rechercher
			</button>
			<button type="button" onClick={handleReset} className="reinit" disabled={isUpdatingFilters}></button>
        </div>
      </form>
    </div>
  );
};

export default TyreFilters; 